import React from "react";
import {
  Ban,
  Bike,
  ChefHat,
  CircleCheck,
  Clock,
  Eye,
  Hourglass,
  MessageCircle,
  MoreVertical,
  PackageCheck,
  Printer,
  Store,
  Ticket,
  Truck,
  Wallet,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import orderService from "@/services/orderService";
import { cn } from "@/lib/utils";
import { formatCurrency, formatElapsed } from "@/lib/format";
import { ACTIVE_STATUSES, NEXT_STATUS, summarizeItems } from "./dashboardMetrics";

/** Visual de cada status em andamento */
const STATUS_STYLES = {
  Aguardando: { icon: Hourglass, className: "border-terra/20 bg-terra-soft text-terra" },
  Preparando: { icon: ChefHat, className: "border-primary/20 bg-secondary text-primary", pulse: true },
  Liberado: { icon: Bike, className: "border-brand-pistachio/40 bg-brand-pistachio/20 text-brand-dark" },
  Entregue: { icon: PackageCheck, className: "border-border bg-muted text-foreground" },
};

const NEXT_ICONS = {
  Preparando: ChefHat,
  Liberado: Bike,
  Entregue: PackageCheck,
  Finalizado: CircleCheck,
};

const isDelivery = (order) => order.delivery_type?.toLowerCase() === "delivery";

/**
 * Card compacto de pedido em andamento (padrão do mockup da home):
 * código + status, cliente, itens, total/pagamento, tempo e ações.
 */
const ActiveOrderCard = ({
  order,
  now,
  busy = false,
  onChangeStatus,
  onCancel,
  onPrint,
  onWhatsApp,
  onDetails,
  onPayment,
}) => {
  const style = STATUS_STYLES[order.status] || STATUS_STYLES.Entregue;
  const StatusIcon = style.icon;
  const next = NEXT_STATUS[order.status];
  const NextIcon = next ? NEXT_ICONS[next.status] : CircleCheck;
  const createdAt = orderService.parseDateTime(order.date_time);
  const items = summarizeItems(order);
  const delivery = isDelivery(order);
  // Pedido saiu para entrega: contato com o cliente vira a ação secundária
  const contactFirst = order.status === "Liberado";

  return (
    <article className="flex flex-col gap-2 rounded-xl border bg-card p-3 shadow-soft">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-primary">#{order.code}</span>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                style.className,
              )}
            >
              {style.pulse ? (
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              ) : (
                <StatusIcon className="h-3 w-3" />
              )}
              {order.status}
            </span>
          </div>
          <span className="mt-0.5 truncate text-sm font-bold text-foreground">
            {order.client?.name || "Cliente não informado"}
          </span>
          <span className="truncate text-xs text-muted-foreground" title={items}>
            {items || "Sem itens"}
          </span>
        </div>
        <div className="flex-shrink-0 text-right">
          <span className="block font-display text-sm font-bold text-foreground">
            {formatCurrency(orderService.calculateTotal(order))}
          </span>
          <span
            className={cn(
              "text-[11px] font-semibold",
              order.payed ? "text-primary" : "text-terra",
            )}
          >
            {order.pay_method || "Pagamento"} {order.payed ? "Pago" : "pendente"}
          </span>
        </div>
      </div>

      {/* Entrega, cupom e tempo do pedido */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          {delivery ? <Truck className="h-3.5 w-3.5" /> : <Store className="h-3.5 w-3.5" />}
          {delivery ? "Delivery" : order.delivery_type || "Retirada"}
          {delivery && parseFloat(order.freight_value) > 0 && (
            <span>({formatCurrency(order.freight_value)})</span>
          )}
        </span>
        {order.coupon_code && (
          <span className="inline-flex items-center gap-1">
            <Ticket className="h-3.5 w-3.5" />
            {order.coupon_code}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-2">
        <div
          className="flex min-w-0 items-center gap-1 text-xs font-semibold text-primary"
          title={order.date_time}
        >
          <Clock className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{createdAt ? formatElapsed(createdAt, now) : order.date_time}</span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => (contactFirst ? onWhatsApp(order) : onPrint(order))}
            className="flex h-9 items-center gap-1 rounded-lg border bg-surface px-2.5 text-xs font-semibold text-foreground transition-all hover:bg-muted active:scale-95"
            title={contactFirst ? "WhatsApp do cliente" : "Imprimir comanda"}
          >
            {contactFirst ? (
              <MessageCircle className="h-4 w-4 text-primary" />
            ) : (
              <Printer className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="hidden min-[400px]:inline">
              {contactFirst ? "Whats" : "Imprimir"}
            </span>
          </button>
          {next && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onChangeStatus(order, next.status)}
              className="flex h-9 items-center gap-1 rounded-lg bg-primary px-3 text-xs font-bold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:pointer-events-none disabled:opacity-60"
              title={`Mudar para "${next.status}"`}
            >
              <NextIcon className="h-4 w-4" />
              {busy ? "Salvando..." : next.label}
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={busy}
                aria-label={`Mais ações do pedido ${order.code}`}
                className="flex h-9 w-9 items-center justify-center rounded-lg border bg-surface text-muted-foreground transition-colors hover:bg-muted disabled:opacity-60"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => onDetails(order)}>
                <Eye className="mr-2 h-4 w-4" />
                Ver detalhes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onPayment(order)}>
                <Wallet className="mr-2 h-4 w-4" />
                {order.payed ? "Pagamento" : "Confirmar pagamento"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onWhatsApp(order)}>
                <MessageCircle className="mr-2 h-4 w-4" />
                WhatsApp do cliente
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onPrint(order)}>
                <Printer className="mr-2 h-4 w-4" />
                Imprimir comanda
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Mudar status
              </DropdownMenuLabel>
              {ACTIVE_STATUSES.filter(
                (status) => status !== "Aguardando" && status !== order.status,
              ).map((status) => {
                const Icon = STATUS_STYLES[status].icon;
                return (
                  <DropdownMenuItem key={status} onClick={() => onChangeStatus(order, status)}>
                    <Icon className="mr-2 h-4 w-4" />
                    {status}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuItem onClick={() => onChangeStatus(order, "Finalizado")}>
                <CircleCheck className="mr-2 h-4 w-4 text-primary" />
                Finalizar
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onCancel(order)}
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              >
                <Ban className="mr-2 h-4 w-4" />
                Cancelar pedido
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </article>
  );
};

export default ActiveOrderCard;
